import React, { useCallback, useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useSchoolId } from '../lib/auth/schoolContext';
import { ROLES, useUserRole, hasRole } from '../lib/auth/roles';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import toast from 'react-hot-toast';

const EMPTY_FORM = {
  meetingType: 'TAC',
  date: '',
  attendees: '',
  progress: '',
  areasForDevelopment: '',
  agreedActions: '',
  linkedCycleNumber: '',
};

export default function TacMeetingsPanel({ studentId }) {
  const { schoolId } = useSchoolId();
  const { role } = useUserRole();
  const canEdit = hasRole(role, [ROLES.SENCO]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadMeetings = useCallback(async () => {
    if (!studentId || !schoolId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'tacMeetings'),
        where('schoolId', '==', schoolId),
        where('studentId', '==', studentId)
      ));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setMeetings(list);
    } catch (err) {
      console.error(err);
      toast.error('Could not load meetings');
    } finally {
      setLoading(false);
    }
  }, [studentId, schoolId]);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({
      meetingType: m.meetingType || 'TAC',
      date: m.date || '',
      attendees: (m.attendees || []).join(', '),
      progress: m.progress || '',
      areasForDevelopment: m.areasForDevelopment || '',
      agreedActions: m.agreedActions || '',
      linkedCycleNumber: m.linkedCycleNumber ? String(m.linkedCycleNumber) : '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!canEdit || !studentId || !schoolId) return;
    setSaving(true);
    try {
      const payload = {
        studentId,
        schoolId,
        meetingType: form.meetingType,
        date: form.date || null,
        attendees: form.attendees.split(',').map((s) => s.trim()).filter(Boolean),
        progress: form.progress || null,
        areasForDevelopment: form.areasForDevelopment || null,
        agreedActions: form.agreedActions || null,
        linkedCycleNumber: form.linkedCycleNumber ? Number(form.linkedCycleNumber) : null,
        updatedAt: serverTimestamp(),
      };
      if (editingId) {
        await updateDoc(doc(db, 'tacMeetings', editingId), payload);
        toast.success('Meeting updated');
      } else {
        await addDoc(collection(db, 'tacMeetings'), { ...payload, createdAt: serverTimestamp() });
        toast.success('Meeting recorded');
      }
      setShowForm(false);
      await loadMeetings();
    } catch (err) {
      toast.error(err?.message || 'Could not save meeting');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!canEdit || !window.confirm('Delete this meeting record?')) return;
    try {
      await deleteDoc(doc(db, 'tacMeetings', id));
      toast.success('Meeting deleted');
      await loadMeetings();
    } catch (err) {
      toast.error('Could not delete meeting');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Multi-agency meetings</CardTitle>
          <p className="text-sm text-gray-500 mt-1">Evidence of multi-agency consultation (TAC, TAF, EHCP review, or other inter-agency meetings).</p>
        </div>
        {canEdit && (
          <button type="button" onClick={openAdd} className="rounded-md bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700">
            Record meeting
          </button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : meetings.length === 0 ? (
          <p className="text-sm text-gray-500">No multi-agency meetings recorded yet.</p>
        ) : (
          <div className="space-y-3">
            {meetings.map((m) => (
              <div key={m.id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <div className="flex justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">{m.meetingType}, {m.date || 'Date not set'}</p>
                    {m.attendees?.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">Attendees: {m.attendees.join(', ')}</p>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex gap-2 shrink-0">
                      <button type="button" onClick={() => openEdit(m)} className="text-xs text-indigo-600 hover:underline">Edit</button>
                      <button type="button" onClick={() => handleDelete(m.id)} className="text-xs text-rose-600 hover:underline">Delete</button>
                    </div>
                  )}
                </div>
                {m.progress && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-gray-700">Progress</p>
                    <p className="text-sm whitespace-pre-wrap">{m.progress}</p>
                  </div>
                )}
                {m.areasForDevelopment && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-gray-700">Areas for development</p>
                    <p className="text-sm whitespace-pre-wrap">{m.areasForDevelopment}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {showForm && canEdit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900">
              <h4 className="text-lg font-semibold mb-4">{editingId ? 'Edit meeting' : 'Record multi-agency meeting'}</h4>
              <div className="space-y-3 text-sm">
                <label className="block">
                  Meeting type
                  <select value={form.meetingType} onChange={(e) => setForm((f) => ({ ...f, meetingType: e.target.value }))} className="mt-1 w-full rounded border px-2 py-1.5">
                    <option value="TAC">TAC</option>
                    <option value="TAF">TAF</option>
                    <option value="Other">Other</option>
                  </select>
                </label>
                <label className="block">
                  Date
                  <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="mt-1 w-full rounded border px-2 py-1.5" />
                </label>
                <label className="block">
                  Attendees (comma-separated)
                  <input value={form.attendees} onChange={(e) => setForm((f) => ({ ...f, attendees: e.target.value }))} className="mt-1 w-full rounded border px-2 py-1.5" />
                </label>
                <label className="block">
                  Progress
                  <textarea value={form.progress} onChange={(e) => setForm((f) => ({ ...f, progress: e.target.value }))} rows={3} className="mt-1 w-full rounded border px-2 py-1.5" />
                </label>
                <label className="block">
                  Areas for development (including new specialist advice)
                  <textarea value={form.areasForDevelopment} onChange={(e) => setForm((f) => ({ ...f, areasForDevelopment: e.target.value }))} rows={3} className="mt-1 w-full rounded border px-2 py-1.5" />
                </label>
                <label className="block">
                  Agreed actions
                  <textarea value={form.agreedActions} onChange={(e) => setForm((f) => ({ ...f, agreedActions: e.target.value }))} rows={2} className="mt-1 w-full rounded border px-2 py-1.5" />
                </label>
                <label className="block">
                  Linked APDR cycle (optional)
                  <select value={form.linkedCycleNumber} onChange={(e) => setForm((f) => ({ ...f, linkedCycleNumber: e.target.value }))} className="mt-1 w-full rounded border px-2 py-1.5">
                    <option value="">None</option>
                    <option value="1">Term 1 / Autumn</option>
                    <option value="2">Term 2 / Spring</option>
                    <option value="3">Term 3 / Summer</option>
                  </select>
                </label>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="rounded border px-3 py-2 text-sm">Cancel</button>
                <button type="button" onClick={handleSave} disabled={saving} className="rounded bg-indigo-600 px-3 py-2 text-sm text-white disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
